import uuid from 'uuid';

import {
  EVT_CTA_CLICK,
  EVT_PLAY,
  EVT_READY,
  EVT_SESSION_END,
  EVT_SESSION_START,
} from 'constants/videoAnalyticsEvents';

import SessionStorage from './sessionStorage';
import { trackCtaClick } from './mediaAnalyticsEvent';

export class VideoSession {
  enabled = false;
  hasStarted = false;
  listeners = [];
  videoRef;
  // fix a race condition where
  // the onloadmetadata is fired too early
  // and the ready event is never sent
  readyEventSent = false;

  start({ videoId, userId, trimId }) {
    // we can only start one video session per user per mount
    if (this.hasStarted) {
      return;
    }

    this.hasStarted = true;
    this.enabled = true;
    this.userId = userId;
    this.videoId = videoId;
    // needs to be a string or graphQL goes bananas
    // Expected type Int at value.trimId; Int cannot represent non 32-bit signed integer value
    // fuck this, string it is :/
    this.trimId = trimId ? `${trimId}` : null;
    this.storage = new SessionStorage(videoId);

    this.startSession();
    this.listenForUnload();
  }

  listenForUnload() {
    window.addEventListener('pagehide', () => this.endSession());
  }

  startSession() {
    this.endStaleSession();

    const timestamp = Date.now();
    const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // offset could end up being -0 :/
    const tzOffset = parseInt(new Date().getTimezoneOffset() / -60, 10);
    const key = this.storage.sessionKey();
    const sessionData = {
      id: uuid.v4(), // unique identifier
      videoId: this.videoId,
      trimId: this.trimId,
      userAgent: navigator.userAgent, // the raw user agent used to compute device and browser
      tzName, // client timezone as reported by the browser
      tzOffset, // client timezone as reported by the browser
      referer: document.referrer, // url where the video has been embedded `document.referer`
      timestamp, // timestamp indicating session start
    };

    this.storage.setKey(key, sessionData);
    this.sessionData = sessionData;
    this.sendEvent(EVT_SESSION_START, sessionData);
  }

  endSession(dropSession = false) {
    if (!this.enabled) {
      return;
    }

    const key = this.storage.sessionKey();
    const { currentTime = 0, paused = false } = this.videoRef || {};

    this.sessionData.dropSession = dropSession;
    this.sessionData.currentTime = currentTime;
    this.sessionData.isPaused = paused;
    this.sessionData.played = this.videoPlayedTimeRanges();
    this.sendEvent(EVT_SESSION_END, this.sessionData);
    this.storage.removeKey(key);

    if (dropSession === true) {
      this.enabled = false;
    }
  }

  updateSession(data) {
    const sessionData = { ...this.sessionData, ...data };
    const key = this.storage.sessionKey();

    this.storage.setKey(key, sessionData);
    this.sessionData = sessionData;
  }

  // if we have a previous session in storage that wasn't properly ended
  // let's do it now before starting a new one.
  endStaleSession() {
    const sessionData = this.storage.fetchSession();

    if (!sessionData) {
      return;
    }

    const { userId } = this;

    this.sessionData = sessionData;

    if (userId && sessionData.userId !== userId) {
      this.updateSession({ userId });
    }

    this.endSession();
  }

  sendEvent(eventName, metadata = {}) {
    // handle feature flagging
    if (!this.enabled) {
      return;
    }

    if (eventName === EVT_READY && !metadata?.duration) {
      return;
    }

    const timestamp = Date.now();

    if (metadata && metadata.currentTime) {
      // round to half a second to avoid inconsinstencies with events
      // fired multiple times in a matter of ms
      metadata.currentTime = Math.round(metadata.currentTime);
    }

    if (eventName === EVT_CTA_CLICK) {
      // TEMP (claudio): for the time being also send event the old way.
      // change this to return eventually.
      trackCtaClick(this.videoId, this.sessionData.id);
    }

    const event = {
      eventName,
      metadata,
      timestamp,
      sessionId: this.sessionData.id,
    };

    if (eventName === EVT_READY) {
      this.readyEventSent = true;
    }

    if (eventName === EVT_PLAY && !this.readyEventSent) {
      console.warn('onloadedmetadata fired too early?');
      this.artificialReadyEvent();
    }

    this.emit(event);
  }

  setVideoRef(ref) {
    this.videoRef = ref;

    if (this.readyEventSent) {
      this.artificialReadyEvent();
    }
  }

  artificialReadyEvent() {
    this.sendEvent(EVT_READY, { duration: this.videoRef?.duration });
  }

  videoPlayedTimeRanges() {
    if (!this.videoRef) {
      return;
    }

    const { played } = this.videoRef;

    if (!played || !played.length) {
      return;
    }

    return JSON.stringify(
      [...Array(played.length)].map((_, i) => [played.start(i), played.end(i)])
    );
  }

  on(events = [], fn) {
    this.listeners.push({ events, fn });
  }

  emit(event = {}) {
    this.listeners.forEach(listener => {
      const { events = [] } = listener;
      const { eventName } = event;
      const shouldSubscribe = !events.length || events.includes(eventName);

      shouldSubscribe && listener.fn(event, this.sessionData, this.videoRef);
    });
  }
}
